// Kompas Dnia — daily recommendation templates.
// One base + 2 variants per result per language, per spec.
// The variant is chosen deterministically from (date, result) so the same
// day shows the same recommendation across reloads.

import { pickVariantIndex } from "@/lib/compass";

const TEMPLATES = {
  act: {
    pl: [
      "Masz warunki do działania. Wybierz jedną najważniejszą rzecz i skup się na niej — reszta może poczekać.",
      "Dziś wystarczy jeden dobry krok. Wykorzystaj energię tam, gdzie naprawdę robi różnicę.",
      "Masz jasność i siłę. Zamiast rozpraszać się na wielu sprawach, zrób jedną rzecz naprawdę dobrze.",
    ],
    en: [
      "You have the conditions to act. Choose one important thing and focus — the rest can wait.",
      "One good step is enough today. Use your energy where it truly matters.",
      "You have clarity and strength. Instead of scattering across many tasks, do one thing well.",
    ],
  },
  rest: {
    pl: [
      "Dzisiejszy dzień może wymagać spokojniejszego tempa. Zmniejsz oczekiwania i pozwól sobie na regenerację.",
      "Nie musisz dziś udowadniać wszystkiego. Wybierz minimum, które naprawdę musi się wydarzyć, resztę potraktuj łagodniej.",
      "Zwolnij bez poczucia winy. Odpoczynek dzisiaj to inwestycja w jutro.",
    ],
    en: [
      "Today may require a slower rhythm. Lower expectations and allow yourself to recover.",
      "You do not need to prove everything today. Choose the minimum that truly needs to happen and let the rest be lighter.",
      "Slow down without guilt. Rest today is an investment in tomorrow.",
    ],
  },
  stay: {
    pl: [
      "Nie potrzebujesz dziś rewolucji. Zrób kolejny mały krok i nie komplikuj dnia bardziej, niż trzeba.",
      "Jesteś w fazie kontynuacji, nie zmian. Trzymaj kurs — dobrze idziesz.",
      "Spokojna kontynuacja to często najmocniejszy ruch. Nie musisz nic udowadniać.",
    ],
    en: [
      "You do not need a revolution today. Take the next small step and do not overcomplicate things.",
      "You are in a phase of continuation, not change. Stay the course — you are doing well.",
      "Calm continuation is often the strongest move. You do not need to prove anything.",
    ],
  },
  change: {
    pl: [
      "Nie walcz z nierealnym planem. Wybierz jedną rzecz, usuń jedną rzecz i przesuń jedną rzecz na później.",
      "Plan może być dobry, ale tempo wymaga korekty. Uprość dzień i wybierz to, co naprawdę ważne.",
      "Nie problem energii, ale sposobu. Uprość, przesuń, zwolnij oczekiwania.",
    ],
    en: [
      "Do not fight an unrealistic plan. Choose one thing, remove one thing and move one thing to later.",
      "The plan may be good, but the pace needs adjustment. Simplify the day and choose what truly matters.",
      "It is not an energy problem, but a method problem. Simplify, move, ease expectations.",
    ],
  },
  pause: {
    pl: [
      "Nie podejmuj dziś wszystkiego naraz. Zatrzymaj się, uporządkuj myśli i wybierz tylko najbliższy spokojny krok.",
      "To może być dzień na odzyskanie jasności. Nie musisz teraz decydować — daj sobie ciszę.",
      "Zatrzymanie się nie jest porażką. To odzyskanie kompasu.",
    ],
    en: [
      "Do not take on everything at once. Pause, organize your thoughts and choose only the next calm step.",
      "Today may be about recovering clarity. You do not have to decide now — give yourself silence.",
      "Pausing is not failure. It is finding your compass again.",
    ],
  },
};

/**
 * Returns a single recommendation string for (result, dateISO, language).
 * Deterministic per (dateISO, result).
 */
export function generateDailyRecommendation(result, dateISO, language = "pl") {
  const bucket = TEMPLATES[result];
  if (!bucket) return "";
  const pool = bucket[language] || bucket.pl;
  const idx = pickVariantIndex(`${dateISO}-${result}`, pool.length);
  return pool[idx];
}
